import store from '@/store'
import { requestOpenAI } from './openaiApi'
import { requestGemini } from './geminiApi'
import { requestClaude } from './claudeApi'

const sendRequest = (aiVersion, params) => {
  if (aiVersion < 10) {
    return requestOpenAI(params)
  }

  if (aiVersion < 15) {
    return requestGemini(params)
  }

  return requestClaude(params)
}

// Dimensions attached to each AI call for usage/cost telemetry. Read from the
// store so callers don't have to thread them through. `useCase` is optional and
// passed by the caller where known (e.g. 'generateEntities', 'chatAssistant').
const buildUsageContext = (useCase = null) => {
  const workflow = store.state.workflow?.workflow || {}
  const projectId = workflow.projectId || null
  // Prefer the org-native activeProject (gated getProject) for org attribution; fall
  // back to the legacy membership list, which is null for org users until populated —
  // previously that left organisationId null and AI usage rows attributed to "Unknown".
  const activeProject = store.state.projectMemberships?.activeProject
  let organisationId = (activeProject && activeProject.id === projectId) ? activeProject.organisationId : null
  if (!organisationId && projectId) {
    const memberships = store.state.projectMemberships?.projectMemberships || []
    organisationId = memberships.find((m) => m.project?.id === projectId)?.project?.organisationId || null
  }

  return {
    userId: store.state.userId || null,
    organisationId: organisationId || null,
    projectId,
    workflowId: workflow.id || null,
    useCase
  }
}

const checkParseError = (obj) => {
  if (obj?.message && obj.message.includes('Failed to parse AI response')) {
    store.commit('snackbar/showMessage', {
      content: obj.message || 'AI failed to generate a valid response',
      timeout: 6000,
      color: 'red'
    }, { root: true })
    throw new Error(obj.message)
  }
}

export const fetchJsonResponse = async ({ messages, attachments, aiVersion, tools, signal, useCase = null }) => {
  const params = {
    messages,
    attachments,
    aiVersion,
    tools,
    streamText: false,
    returnReader: false,
    signal,
    usageContext: buildUsageContext(useCase)
  }

  return sendRequest(aiVersion, params)
}

export const streamJsonResponses = async ({ messages, attachments, aiVersion, onChunkReceived, signal, useCase = null }) => {
  if (typeof onChunkReceived !== 'function') {
    throw new Error('streamJsonResponses: onChunkReceived callback is required.')
  }

  const allReceivedObjects = []
  const params = {
    messages,
    attachments,
    aiVersion,
    streamText: false,
    returnReader: true,
    signal,
    usageContext: buildUsageContext(useCase)
  }

  const reader = await sendRequest(aiVersion, params)

  if (!reader) {
    return []
  }

  const decoder = new TextDecoder()
  let buffer = ''
  const delimiter = 'jsonobjectdelimiter'

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel()
        break
      }

      const { value, done } = await reader.read()

      if (done) {
        const trimmedBuffer = buffer.trim()

        if (trimmedBuffer) {
          try {
            const finalObj = JSON.parse(trimmedBuffer)
            checkParseError(finalObj)
            await onChunkReceived(finalObj)
            allReceivedObjects.push(finalObj)
          } catch (e) {
            if (e.message && e.message.includes('Failed to parse AI response')) {
              throw e
            }
            console.error('Error parsing final buffered JSON chunk:', e, 'Buffer:', trimmedBuffer)
          }
        }
        break
      }

      const textChunk = decoder.decode(value, { stream: true })

      let response
      try {
        response = JSON.parse(textChunk)
      } catch (e) {
        // Not JSON, likely a text chunk
      }

      if (response?.error?.message) {
        throw new Error(response.error.message)
      }

      buffer += textChunk

      let delimiterIndex
      while ((delimiterIndex = buffer.indexOf(delimiter)) !== -1) {
        const jsonString = buffer.substring(0, delimiterIndex)
        buffer = buffer.substring(delimiterIndex + delimiter.length)

        const trimmedJsonString = jsonString.trim()

        if (trimmedJsonString) {
          try {
            const obj = JSON.parse(trimmedJsonString)
            checkParseError(obj)
            await onChunkReceived(obj)
            allReceivedObjects.push(obj)
          } catch (e) {
            if (e.message && e.message.includes('Failed to parse AI response')) {
              throw e
            }
            console.error('Error parsing streamed JSON object:', e, 'Failed JSON string:', trimmedJsonString)
          }
        }
      }
    }
  } catch (error) {
    if (!reader.closed && signal && !signal.aborted) {
      await reader.cancel()
    }
    throw error
  }

  return allReceivedObjects
}

export const streamTextResponse = async ({ messages, aiVersion, onChunkReceived, signal, useCase = null }) => {
  if (typeof onChunkReceived !== 'function') {
    throw new Error('streamTextResponse: onChunkReceived callback is required.')
  }

  const params = {
    messages,
    aiVersion,
    streamText: true,
    returnReader: true,
    signal,
    usageContext: buildUsageContext(useCase)
  }

  const reader = await sendRequest(aiVersion, params)

  if (!reader) {
    return
  }

  const decoder = new TextDecoder()

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel()
        break
      }

      const { value, done } = await reader.read()

      if (done) {
        break
      }

      const textChunk = decoder.decode(value, { stream: true })

      let response
      try {
        response = JSON.parse(textChunk)
      } catch (e) {
        // Not JSON, likely a text chunk
      }

      if (response?.error?.message) {
        throw new Error(response.error.message)
      }

      await onChunkReceived(textChunk)
    }
  } catch (error) {
    if (!reader.closed && signal && !signal.aborted) {
      await reader.cancel()
    }
    throw error
  }
}
